import { Component, useCallback, useRef } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import { toast } from 'sonner'

/**
 * Feature-level error boundary for the Milkdown editor. Milkdown/ProseMirror
 * teardown races (React strict-mode double-mount, rapid tab/card toggles) can
 * throw during render; this keeps the surrounding card usable instead of
 * crashing the route.
 */
export class MilkdownErrorBoundary extends Component<
  {
    onError: (error: unknown) => void
    children: ReactNode
  },
  { hasError: boolean }
> {
  override state = { hasError: false }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true }
  }

  override componentDidCatch(error: Error, _info: ErrorInfo) {
    this.props.onError(error)
  }

  override render() {
    if (this.state.hasError) {
      return (
        <div className="p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md">
          Editor crashed. Please refresh or reopen this page.
        </div>
      )
    }

    return this.props.children
  }
}

/** Toasts the first editor error only (later teardown errors are noisy). */
export function useToastEditorErrorOnce() {
  const toastShownRef = useRef(false)
  return useCallback((error: unknown) => {
    if (toastShownRef.current) return
    toastShownRef.current = true

    const message = error instanceof Error ? error.message : String(error)
    toast.error('Editor error', { description: message })
    console.error('Milkdown editor error:', error)
  }, [])
}
